import Seo from "@/components/Seo";

/**
 * NOTE FOR DEVELOPERS:
 * Legal pages are starter content only and should be reviewed by a qualified
 * legal professional before launch.
 */
export default function Terms() {
    return (
        <div data-testid="page-terms">
            <Seo
                title="Terms of Use | Chief AI Partners"
                description="The Terms of Use for the Chief AI Partners website. Governed by the laws of New South Wales, Australia."
                path="/terms"
            />

            <section className="cap-section pt-24 md:pt-32 pb-12">
                <div className="cap-container">
                    <div className="cap-eyebrow mb-6">Legal</div>
                    <h1 className="font-serif text-navy text-5xl md:text-6xl leading-[1.02] tracking-tight max-w-3xl">
                        Terms of Use
                    </h1>
                    <p className="text-slate text-sm mt-4">Last updated: April 2026</p>
                </div>
            </section>

            <section className="pb-24">
                <div className="cap-container max-w-3xl text-slate leading-relaxed space-y-10">
                    <p className="text-base md:text-lg">
                        These Terms of Use govern your use of the website chiefai.partners, operated by Sanlux Pty Ltd trading as Chief AI Partners.
                    </p>
                    <p className="text-base md:text-lg">
                        By accessing or using this website, you agree to these Terms.
                    </p>

                    <LegalBlock number="1" title="Use of this website">
                        <p>You agree to use this website for lawful purposes only and in a way that does not infringe the rights of others or restrict their use of the site.</p>
                        <p>We may update, modify or discontinue any part of the website at any time without notice.</p>
                    </LegalBlock>

                    <LegalBlock number="2" title="Information only">
                        <p>The content on this website is provided for general information purposes only.</p>
                        <p>It does not constitute professional, legal, financial or technical advice.</p>
                        <p>Any engagement with Chief AI Partners is governed by a separate written agreement.</p>
                    </LegalBlock>

                    <LegalBlock number="3" title="Intellectual property">
                        <p>All content on this website, including text, graphics, design and the Helm Framework, is owned by or licensed to Sanlux Pty Ltd.</p>
                        <p>You may not reproduce, distribute or use any content from this website without prior written permission.</p>
                    </LegalBlock>

                    <LegalBlock number="4" title="Limitation of liability">
                        <p>To the maximum extent permitted by law, Chief AI Partners is not liable for any loss or damage arising from:</p>
                        <BulletList
                            items={[
                                "your use of, or reliance on, this website",
                                "any errors or omissions in the content",
                                "any interruption or unavailability of the website",
                            ]}
                        />
                        <p>Nothing in these Terms excludes any rights that cannot be excluded under Australian law.</p>
                    </LegalBlock>

                    <LegalBlock number="5" title="Third-party links">
                        <p>This website may contain links to third-party websites.</p>
                        <p>We are not responsible for the content, policies or practices of those websites.</p>
                    </LegalBlock>

                    <LegalBlock number="6" title="Privacy">
                        <p>
                            Your use of this website is also governed by our{" "}
                            <a href="/privacy" className="underline text-navy hover:text-teal">Privacy Policy</a>.
                        </p>
                    </LegalBlock>

                    <LegalBlock number="7" title="Governing law">
                        <p>These Terms are governed by the laws of New South Wales, Australia.</p>
                        <p>You submit to the exclusive jurisdiction of the courts of New South Wales.</p>
                    </LegalBlock>

                    <LegalBlock number="8" title="Contact">
                        <p>If you have any questions about these Terms, please contact:</p>
                        <div className="mt-3 space-y-1 text-navy">
                            <div className="font-serif text-xl">Chief AI Partners</div>
                            <div>Sanlux Pty Ltd</div>
                            <div>Sydney, Australia</div>
                            <a href="mailto:enquiries@chiefai.partners" className="underline hover:text-teal">
                                enquiries@chiefai.partners
                            </a>
                        </div>
                    </LegalBlock>
                </div>
            </section>
        </div>
    );
}

function LegalBlock({ number, title, children }) {
    return (
        <div data-testid={`terms-section-${number}`}>
            <div className="flex items-baseline gap-4 mb-4">
                <span className="text-xs uppercase tracking-[0.22em] text-teal font-semibold">
                    {number}
                </span>
                <h2 className="font-serif text-navy text-2xl md:text-3xl leading-tight">
                    {title}
                </h2>
            </div>
            <div className="space-y-4 text-base md:text-lg">{children}</div>
        </div>
    );
}

function BulletList({ items }) {
    return (
        <ul className="list-disc pl-6 space-y-1.5">
            {items.map((t) => (
                <li key={t}>{t}</li>
            ))}
        </ul>
    );
}
